import React, { useEffect, useState } from 'react'
import { useSnapshot } from 'valtio'
import appModel from '../../../models/app-model'
import { addToCart } from '../../../utils/cart'
import Spinner from './spinner'
import { approveDesign, updateDesignDetails } from '../../../models/app-actions'
import { getBackendApiUrl, getDWSUrl, getLanguage } from "../../../config"
import { addAuthorizationHeader, getCustomerToken } from '../../../api/network'
import { getDesignFees } from '../../../api/backend'
import { AxiosError } from 'axios'
import { DebossedLeatherProductTypes, FlexStyleProductTypes, WEIPVCProductTypes } from '../../../types/product-types'
import { InternalArtStates } from '../../../types/art-status'
import { CustomerServiceModal } from '../customerServiceMessage'
import CustomModal from '../../../components/customMessageModal/customMsgModal'

// Styles
import styles from './customerWorkflow.module.css'
import { isAWAWProductType } from '../../../utils/artDetailsUtils'
import { CustomPatchesPopup } from './customPatchesPopup'

interface CustomerWorkFlowProps {
  itemId: string;
  docType: string;
  wizardType: string;
  wasPurchased: boolean;
}

const CustomerWorkFlow: React.FC<CustomerWorkFlowProps> = ({ itemId, docType, wizardType, wasPurchased }) => {
  const appSnap = useSnapshot(appModel);
  const wizardDoc = appSnap.activeDesignDoc;
  let wizardDocData = wizardDoc?.wizardData;
  wizardDocData = Array.isArray(wizardDocData) ? wizardDocData[0] : wizardDocData;
  const docId: string = wizardDoc?.id as string;
  const hasAutoFixData = "autoFixData" in wizardDocData;
  const autoFixJobId = hasAutoFixData ? wizardDocData?.autoFixData?.JobId : undefined;
  const statusID: string = wizardDoc?.statusID as string;
  const customerId = appSnap.customerId || "";
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [processing, setProcessing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [unpaidArtFees, setUnpaidArtFees] = useState<boolean>(false);
  const isWizardDesign = docType === "WizardDesign";
  const isCustomProduct = wizardType === "CustomProduct";
  const isCustomPatches = wizardDoc?.wizardData?.serviceType === "CustomPatches";
  const isApproved = wizardDoc?.statusID == InternalArtStates.APPROVED;
  const isIncomplete = wizardDoc?.statusID == InternalArtStates.INCOMPLETE;
  const isFlexStyle = FlexStyleProductTypes.includes(wizardDoc?.wizardID ?? "");
  const isWEIPVC = appSnap.activeDesignDoc?.docType == "WizardDesign" && WEIPVCProductTypes.includes(appSnap.activeDesignDoc.wizardID);
  const isDebossedLeather = DebossedLeatherProductTypes.includes(wizardDoc?.wizardID ?? "");
  const isImported = !!appSnap.activeDesignDoc?.wizardData?.ImportedArtworkID
  const [needsArtTeamApproval, setNeedsArtTeamApproval] = useState<boolean>(false);
  const [openCSMsgModal, setOpenCSMsgModal] = useState<boolean>(false);
  const [currentDocId, setCurrentDocId] = useState<string>("");

  const [missingMaterialModal, setMissingMaterialModal] = useState<undefined|JSX.Element>(undefined);
  const [showCustomPatchesPopup, setShowCustomPatchesPopup] = useState<boolean>(false);

  useEffect(() => {
    if(!isIncomplete) handleFetchFees();
  }, [wizardDoc?.id, isIncomplete]);

  // Get Design Fees 
  const handleFetchFees = async () => {
    if (wizardDoc?.id) {
      setIsLoading(true);
      setProcessing(true);
      try {
        const fees = await getDesignFees(wizardDoc.id);
        const hasUnpaidArtFees = fees?.some(fee => fee.feeType === "ArtFee" && !fee.paid);
        setUnpaidArtFees(hasUnpaidArtFees);
      } catch (err) {
        setError("Failed to fetch Art Charges. Please try again.");
      } finally {
        setProcessing(false);
        setIsLoading(false);
      }
    }
  };

  const handleAddToCart = async (itemId: string, docType: string, wizardType: string, statusID: string) => {
    const isAWAW = docType === "LNTransfer" && isAWAWProductType(wizardType)

    if (isWizardDesign && (wizardType === 'WovenTag')
    ) {
      setCurrentDocId(itemId)
      setOpenCSMsgModal(true);
      return;
    }

    let isOriginalMaterialUnavailable = false;
    const unavailableMaterials = ['FFE','CBL']
    if(isAWAW && (unavailableMaterials.includes(wizardDocData.material1) || unavailableMaterials.includes(wizardDocData.material2) || unavailableMaterials.includes(wizardDocData.material3))) {
      isOriginalMaterialUnavailable = true;
    }

    const wizardTypesWithUnavailableMaterials = ['CustomCadCut','CustomCutLetters','CustomCutNumbers']
    if(docType === "WizardDesign"
      && wizardTypesWithUnavailableMaterials.includes(wizardType)
      && (unavailableMaterials.includes(wizardDocData.color1Material) || unavailableMaterials.includes(wizardDocData.color2Material) || unavailableMaterials.includes(wizardDocData.color3Material))
    ) {
      isOriginalMaterialUnavailable = true;
    }

    if (isOriginalMaterialUnavailable) {
      const modal = <CustomModal
        isOpen={true}
        onClose={() => { setMissingMaterialModal(undefined) }}
        onCancel={() => { setMissingMaterialModal(undefined) }}
        title='Notice'
        formattedMessage={(
          <div className="flex flex-col text-sm gap-y-[1em]">
            <span>
              {"One or more materials used in this design are unavailable."}
            </span>
            <span>
              {"If you continue you will need to select alternative materials for those unavailable."}
            </span>
          </div>
        )}
        onConfirm={async () => {
          try {
            setIsLoading(true);
            await addToCart(itemId, customerId, docType, wizardType, statusID);
            sessionStorage.removeItem("isGoBack");
          } catch (err: any) {
            if (err instanceof AxiosError) {
              const errorData = (err as AxiosError)?.response?.data
              const error = errorData == null ? null : typeof errorData == "object" && "error" in errorData && typeof errorData.error == "string" ? errorData.error : JSON.stringify(errorData)
              setError(error);
            }
            else {
              console.log(err)
              setError(err);
            }
          } finally {
            setIsLoading(false);
            setMissingMaterialModal(undefined);
          }
        }}
        confirmButtonText="Ok"
        showCancelButton={true}
        isCustomerView={false}
      />;

      setMissingMaterialModal(modal);
      return;
    }

    try {
      setIsLoading(true);
      await addToCart(itemId, customerId, docType, wizardType, statusID);
      sessionStorage.removeItem("isGoBack");
    } catch (err: any) {
      if (err instanceof AxiosError) {
        const errorData = (err as AxiosError)?.response?.data
        const error = errorData == null ? null : typeof errorData == "object" && "error" in errorData && typeof errorData.error == "string" ? errorData.error : JSON.stringify(errorData)
        setError(error);
      }
      else {
        console.log(err)
        setError(err);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const openCustomPatchesApprovePopup = () => {
    setShowCustomPatchesPopup(true);
  };

  const updateApprovedState = async (itemId: string) => {
    try {
      setIsLoading(true);
      const updateItemState = await approveDesign(itemId);
      await updateDesignDetails();
      console.log(updateItemState);
    } catch (err: any) {
      let errorMsg: string | null = "";

      if (err instanceof AxiosError) {
        const errorData = (err as AxiosError)?.response?.data
        const error = errorData == null ? null : typeof errorData == "object" && "error" in errorData && typeof errorData.error == "string" ? errorData.error : JSON.stringify(errorData)
        const errorShippingRegex = /^Failed to set the quantities to ship in Business Central. You cannot ship more than 0 units\.  CorrelationId:  [a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}\.$/g

        if (error && errorShippingRegex.test(error)) {
          setNeedsArtTeamApproval(true);
          errorMsg = "The art team must approve this design on your behalf";
        } else {
          errorMsg = error;
        }
      } else {
        errorMsg = String(err);
      }

      console.log(err)
      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const canApprove = appSnap.activeDesignDoc?.statusID == "PEND_C" && !!appSnap.activeDesignDoc?.productionArtworkPreview?.length;
  const canAddToCart = appSnap.activeDesignDoc?.statusID == "APPROVED" || appSnap.activeDesignDoc?.statusID == "REVIEW";
  const canAddProofToCart = appSnap.activeDesignDoc?.statusID == "REVIEW_READY";
  const isReview = appSnap.activeDesignDoc?.statusID == "REVIEW"
  const buttonClasses = `${styles.orangeBtn} flex flex-row gap-1 text-white hover:bg-blue-500`;

  const orderFlexStyleSample = (docId: string) => {
    fetch(`${getBackendApiUrl()}/DesignSamples`, {
      method: 'POST',
      headers: addAuthorizationHeader({
        "content-type": "application/json",
      }),
      body: JSON.stringify({
        id: docId
      })
    }).then(async (samplesResp) => {
      if (!samplesResp.ok) throw new Error("Error getting purchase data " + await samplesResp.text())
      const samplesRespJson = await samplesResp.json()
      const itemGroup = { items: [{ productId: samplesRespJson.itemNo, uomId: samplesRespJson.UofM, ...samplesRespJson }] }

      parent.postMessage(itemGroup, '*');
      console.log('parent.postMessge', JSON.stringify(itemGroup, null, 2))

    }).catch(err => {
      throw err
    })
  }

  const orderWeiPvcSample = (docId: string) => {
    fetch(`${getBackendApiUrl()}/DesignSamples`, {
      method: 'POST',
      headers: addAuthorizationHeader({
        "content-type": "application/json",
      }),
      body: JSON.stringify({
        id: docId
      })
    }).then(async (samplesResp) => {
      if (!samplesResp.ok) throw new Error("Error getting purchase data " + await samplesResp.text())
      const samplesRespJson = await samplesResp.json()
      const itemGroup = { items: [{ productId: samplesRespJson.itemNo, uomId: samplesRespJson.UofM, ...samplesRespJson }] }

      parent.postMessage(itemGroup, '*');
      console.log('parent.postMessge', JSON.stringify(itemGroup, null, 2))

    }).catch(err => {
      throw err
    })
  }

  const orderDebossedLeatherSample = (docId: string) => {
    fetch(`${getBackendApiUrl()}/DesignSamples`, {
      method: 'POST',
      headers: addAuthorizationHeader({
        "content-type": "application/json",
      }),
      body: JSON.stringify({
        id: docId
      })
    }).then(async (samplesResp) => {
      if (!samplesResp.ok) throw new Error("Error getting purchase data " + await samplesResp.text())
      const samplesRespJson = await samplesResp.json()
      const itemGroup = { items: [{ productId: samplesRespJson.itemNo, uomId: samplesRespJson.UofM, ...samplesRespJson }] }

      parent.postMessage(itemGroup, '*');
      console.log('parent.postMessge', JSON.stringify(itemGroup, null, 2))

    }).catch(err => {
      throw err
    })
  }

  const orderCustomPatchesSample = (docId: string) => {
    fetch(`${getBackendApiUrl()}/DesignSamples`, {
      method: 'POST',
      headers: addAuthorizationHeader({
        "content-type": "application/json",
      }),
      body: JSON.stringify({
        id: docId
      })
    }).then(async (samplesResp) => {
      if (!samplesResp.ok) throw new Error("Error getting purchase data " + await samplesResp.text())
      const samplesRespJson = await samplesResp.json()
      const itemGroup = { items: [{ productId: samplesRespJson.itemNo, uomId: samplesRespJson.UofM, ...samplesRespJson }] }

      parent.postMessage(itemGroup, '*');
      console.log('parent.postMessge', JSON.stringify(itemGroup, null, 2))

    }).catch(err => {
      throw err
    })
  }

  const payArtFees = async (docId: string) => {
    setProcessing(true);
    setIsLoading(true);
    setError(null);

    fetch(`${getBackendApiUrl()}/DesignPayArtFees`, {
      method: 'POST',
      headers: addAuthorizationHeader({
        "content-type": "application/json",
      }),
      body: JSON.stringify({
        id: docId
      })
    }).then(async (samplesResp) => {
      if (!samplesResp.ok) throw new Error("Failed to pay Art Fees. " + await samplesResp.text())
      const samplesRespJson = await samplesResp.json()
      const itemGroup = { items: [{ productId: samplesRespJson.itemNo, uomId: samplesRespJson.UofM, ...samplesRespJson }] }

      parent.postMessage(itemGroup, '*');
      console.log('parent.postMessge', JSON.stringify(itemGroup, null, 2))
      updateDesignDetails();

    }).catch(err => {
      setError('Failed to pay Art Fees. Please try again.');
      throw err;
    }).finally(() => {
      setProcessing(false);
      setIsLoading(false);
    })
  };

  const handleResume = (docId: string, autoFixJobId?: string) => {
    //TODO: handle autoFixJobId after the page to do so gets created
    if(autoFixJobId) {
      const DWS_URL = getDWSUrl();
      const dwsWizard = "https://" + DWS_URL;

      const params = new URLSearchParams(window.location.search);
      params.append('source', 'dashboard');
      const cookieValues: string[] = [];

      params.forEach((value, key) => {
        cookieValues.push(`__dws_${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
      });

      localStorage.setItem('dws-cookie', cookieValues.join('; '));

      const currentLang = getLanguage()
      const lang = currentLang ? `&lang=${currentLang}` : ""

      window.location.replace(`${dwsWizard}/index-hawk-dtf-edit-saved.html?customerId=${customerId}&customerToken=${getCustomerToken()}${lang}&designId=${docId}&editJob=${autoFixJobId}`);
      return;
    }

    // Redirect to upload page
    const DWS_URL = getDWSUrl();
    const dwsWizard = "https://" + DWS_URL;

    const params = new URLSearchParams(window.location.search);
    params.append('source', 'dashboard');
    const cookieValues: string[] = [];

    params.forEach((value, key) => {
      cookieValues.push(`__dws_${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    });

    localStorage.setItem('dws-cookie', cookieValues.join('; '));

    const currentLang = getLanguage()
    const lang = currentLang ? `&lang=${currentLang}` : ""

    window.location.replace(`${dwsWizard}?customerId=${customerId}&source=dashboard&customerToken=${getCustomerToken()}${lang}&saved=true&designId=${docId}`);
  }

  return (
    <>
      {error && <div className={styles.error}>{error}</div>}
      <div className={styles.customerWrapper}>
        <Spinner spinning={isLoading} />
        {!isLoading && !processing && (
          <>
            {unpaidArtFees ? (
              <button
                type="button"
                className={buttonClasses}
                onClick={() => payArtFees(docId)}
              >
                Pay Art Fees
              </button>
            ) : (
              <>
                {(!isReview && canApprove && !(isCustomProduct && isWizardDesign)) && (
                  <button
                    type="button"
                    className={[buttonClasses, needsArtTeamApproval ? styles.disabledBtn : ''].join(' ')}
                    onClick={() => isCustomPatches ? openCustomPatchesApprovePopup() : updateApprovedState(itemId)}
                    disabled={needsArtTeamApproval}
                  >
                    I Approve Artwork
                  </button>
                )}
                {canAddToCart && (
                  <button
                    type="button"
                    className={buttonClasses}
                    onClick={() => handleAddToCart(itemId, docType, wizardType, statusID)}
                  >
                    Add To Cart
                  </button>
                )}
                {canAddProofToCart && (
                  <button
                    type="button"
                    className={buttonClasses}
                    onClick={() => handleAddToCart(itemId, docType, wizardType, statusID)}
                  >
                    Add Proof To Cart
                  </button>
                )}
                {isFlexStyle && !wasPurchased && isApproved && !isImported && (
                  <button
                    type="button"
                    className={buttonClasses}
                    onClick={() => orderFlexStyleSample(docId)}
                  >
                    Order Sample
                  </button>
                )}
                {isWEIPVC && !wasPurchased && isApproved && !isImported && (
                  <button
                    type="button"
                    className={buttonClasses}
                    onClick={() => orderWeiPvcSample(docId)}
                  >
                    Order Sample
                  </button>
                )}
                {isCustomPatches && !wasPurchased && isApproved && !isImported && (
                  <button
                    type="button"
                    className={buttonClasses}
                    onClick={() => orderCustomPatchesSample(docId)}
                  >
                    Order Sample
                  </button>
                )}
                {isIncomplete && (
                  <button
                    type="button"
                    className={buttonClasses}
                    onClick={() => handleResume(docId, autoFixJobId)}
                  >
                    Resume Upload
                  </button>
                )}
                {isDebossedLeather && !wasPurchased && isApproved && !isImported && (
                  <button
                    type="button"
                    className={buttonClasses}
                    onClick={() => orderDebossedLeatherSample(docId)}
                  >
                    Order Sample
                  </button>
                )}
              </>
            )}
          </>
        )}
      </div>

      {currentDocId && <CustomerServiceModal
        isOpen={openCSMsgModal}
        onClose={() => setOpenCSMsgModal(false)}
        docId={currentDocId}
        customerId={customerId}
        wizardType={wizardType}
      />}

      {missingMaterialModal}

      {showCustomPatchesPopup && (
        <CustomPatchesPopup
          isOpen={showCustomPatchesPopup}
          onClose={() => setShowCustomPatchesPopup(false)}
          onConfirm={() => { setShowCustomPatchesPopup(false); updateApprovedState(itemId); }}
        />
      )}

    </>
  );
};

export default CustomerWorkFlow;